import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, Bookmark, ChevronDown, ChevronUp } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import {
  fetchHistorical, fetchStockQuote, fetchFundamentals,
  formatPrice, formatIndianCurrency,
  type OHLCCandle, type StockQuote, type StockFundamentals,
} from '@/services/marketDataApi';
import { extractTickerFromQuery, tickerLabel } from '@/lib/stockSearch';

// ── Types ─────────────────────────────────────────────────────────────────
type AiResponseProps = { prompt: string };
type Timeframe = '1D' | '5D' | '1M' | '3M' | '1Y';

const TF_CONFIG: Record<Timeframe, { range: string; interval: string }> = {
  '1D': { range: '1d',  interval: '5m'  },
  '5D': { range: '5d',  interval: '15m' },
  '1M': { range: '1mo', interval: '1d'  },
  '3M': { range: '3mo', interval: '1d'  },
  '1Y': { range: '1y',  interval: '1wk' },
};

// ── Helpers ───────────────────────────────────────────────────────────────

function formatTime(ts: number, tf: Timeframe): string {
  const d = new Date(ts);
  if (tf === '1D' || tf === '5D') {
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Styled primitives ─────────────────────────────────────────────────────
const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white ${className}`}>
    {children}
  </div>
);

const StatCard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
    <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{label}</div>
    <div className="text-lg font-mono font-semibold text-slate-900">{value}</div>
  </div>
);

const PriceBadge: React.FC<{ change: number; changePct: number }> = ({ change, changePct }) => {
  const up = changePct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-mono font-semibold ${up ? 'text-green-600' : 'text-red-600'}`}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {up ? '+' : ''}{change.toFixed(2)} ({up ? '+' : ''}{changePct.toFixed(2)}%)
    </span>
  );
};

// ── Live chart ────────────────────────────────────────────────────────────
const LiveChart: React.FC<{ ticker: string; quote: StockQuote | null }> = ({ ticker, quote }) => {
  const [tf, setTf] = useState<Timeframe>('3M');
  const [candles, setCandles] = useState<OHLCCandle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const { range, interval } = TF_CONFIG[tf];
    fetchHistorical(ticker, range as any, interval as any)
      .then(d => { setCandles(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ticker, tf]);

  const chartData = candles.map(c => ({
    t: formatTime(c.timestamp, tf),
    close: c.close,
    vol: c.volume,
  }));

  const isUp = candles.length >= 2
    ? candles[candles.length - 1].close >= candles[0].close
    : true;

  const strokeColor = isUp ? '#16a34a' : '#dc2626';

  return (
    <Card className="p-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900">
              {quote?.shortName ?? (tickerLabel(ticker))}
            </span>
            <span className="text-xs font-mono text-slate-400">
              {ticker.startsWith('^') ? ticker : `${ticker}.NS`}
            </span>
            <span className="text-[10px] border border-slate-200 text-navy-500 px-1.5 py-0.5 rounded">
              {ticker.startsWith('^') ? 'INDEX' : 'NSE'}
            </span>
          </div>
          {quote && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-mono font-bold text-slate-900">{formatPrice(quote.price)}</span>
              <PriceBadge change={quote.change} changePct={quote.changePct} />
            </div>
          )}
        </div>
        {/* Timeframe tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-slate-100">
          {(Object.keys(TF_CONFIG) as Timeframe[]).map(t => (
            <button
              key={t}
              onClick={() => setTf(t)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all ${
                t === tf
                  ? 'bg-navy-500 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Price area chart */}
      <div className="h-52">
        {loading ? (
          <div className="h-full rounded-xl animate-pulse bg-slate-100" />
        ) : chartData.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">No data available</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={strokeColor} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={strokeColor} stopOpacity={0}   />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" vertical={false} />
              <XAxis dataKey="t" tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false} domain={['auto', 'auto']}
                tickFormatter={v => `₹${(v as number).toLocaleString('en-IN')}`} />
              <RTooltip
                contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '12px', fontFamily: 'monospace' }}
                labelStyle={{ color: '#475569' }}
                formatter={(v: any) => [`₹${(v as number).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, 'Close']}
              />
              <Area type="monotone" dataKey="close" stroke={strokeColor} strokeWidth={2} fill="url(#priceGrad)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Volume bars */}
      {!loading && chartData.length > 0 && (
        <div className="h-12 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 0, right: 4, left: -20, bottom: 0 }}>
              <Bar dataKey="vol" fill="rgba(27,79,216,0.15)" radius={[1,1,0,0]} />
              <XAxis dataKey="t" hide />
              <YAxis hide />
              <RTooltip
                contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '11px' }}
                formatter={(v: any) => [(v as number).toLocaleString('en-IN'), 'Volume']}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

// ── Fundamentals ──────────────────────────────────────────────────────────
const FundamentalsPanel: React.FC<{ f: StockFundamentals | null; loading: boolean }> = ({ f, loading }) => {
  const rows = [
    { k: 'P/E Ratio',      v: f?.pe           != null ? f.pe.toFixed(1)              : null },
    { k: 'P/B Ratio',      v: f?.pb           != null ? f.pb.toFixed(2)              : null },
    { k: 'EPS (TTM)',      v: f?.eps          != null ? formatPrice(f.eps)           : null },
    { k: 'ROE',            v: f?.roe          != null ? `${(f.roe * 100).toFixed(1)}%` : null },
    { k: 'Debt/Equity',    v: f?.debtToEquity != null ? f.debtToEquity.toFixed(2)   : null },
    { k: 'Div. Yield',     v: f?.dividendYield!= null ? `${(f.dividendYield*100).toFixed(2)}%` : null },
    { k: 'Rev. Growth',    v: f?.revenueGrowth!= null ? `${(f.revenueGrowth*100).toFixed(1)}%` : null },
    { k: 'Profit Margin',  v: f?.profitMargin != null ? `${(f.profitMargin*100).toFixed(1)}%`  : null },
    { k: 'Market Cap',     v: f?.marketCap    != null ? formatIndianCurrency(f.marketCap) : null },
  ];

  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">Fundamentals</h3>
      {loading ? (
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg animate-pulse bg-slate-100" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {rows.map(({ k, v }) => (
            <div key={k} className="rounded-lg p-3 bg-slate-50 border border-slate-100">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">{k}</div>
              <div className="text-sm font-mono font-semibold text-slate-900">{v ?? '—'}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

type Signal = { name: string; value: string; state: string; up: boolean | null };

const SIGNALS_FALLBACK: Signal[] = [
  { name: 'RSI (14)', value: '56',        state: 'Neutral', up: null },
  { name: 'MACD',     value: '+0.12',     state: 'Bullish', up: true },
  { name: '20-EMA',   value: 'Above',     state: 'Bullish', up: true },
  { name: 'ADX (14)', value: '18',        state: 'Neutral', up: null },
  { name: 'Bollinger',value: 'Mid-band',  state: 'Neutral', up: null },
];

const NEWS = [
  { title: 'IT stocks extend gains on stable US outlook',  src: 'Market Context', time: '2h',  pos: true  },
  { title: 'Midcaps consolidate after sharp run-up',       src: 'Market Context', time: '4h',  pos: null  },
  { title: 'Banking sees profit-booking at highs',         src: 'Market Context', time: '6h',  pos: false },
  { title: 'FII inflows boost market sentiment',           src: 'Market Context', time: '8h',  pos: true  },
  { title: 'Auto sector shows recovery signs',             src: 'Market Context', time: '12h', pos: true  },
];

// ── Main component ────────────────────────────────────────────────────────
const AiResponse: React.FC<AiResponseProps> = ({ prompt }) => {
  const ticker = useMemo(() => extractTickerFromQuery(prompt), [prompt]);

  const [quote,        setQuote]        = useState<StockQuote | null>(null);
  const [fundamentals, setFundamentals] = useState<StockFundamentals | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [fundLoading,  setFundLoading]  = useState(true);
  const [activeTab,    setActiveTab]    = useState<'technicals'|'news'|'faq'>('technicals');
  const [faqOpen,      setFaqOpen]      = useState<number | null>(null);
  const [signals,      setSignals]      = useState<Signal[]>([]);
  const [signalsLive,  setSignalsLive]  = useState(false);
  const [signalsLoading, setSignalsLoading] = useState(false);

  const summaryLines = [
    `Analysing: "${prompt}"`,
    'Fetching live quote and OHLC data from Yahoo Finance.',
    'Loading fundamentals — P/E, ROE, margins, debt/equity.',
    'Fetching live technical signals from TwelveData.',
    'Analysis ready. See chart and panels below.',
  ];
  const [shownLines, setShownLines] = useState(0);

  useEffect(() => {
    setQuoteLoading(true);
    setFundLoading(true);
    setSignalsLoading(true);
    setSignals([]);
    setSignalsLive(false);
    setShownLines(0);

    fetchStockQuote(ticker).then(q => { setQuote(q); setQuoteLoading(false); }).catch(() => setQuoteLoading(false));
    if (!ticker.startsWith('^')) {
      fetchFundamentals(ticker).then(f => { setFundamentals(f); setFundLoading(false); }).catch(() => setFundLoading(false));
    } else {
      setFundLoading(false);
    }

    fetch(`/.netlify/functions/technicals?symbol=${encodeURIComponent(ticker)}`)
      .then(r => r.json())
      .then(data => {
        if (data.supported && data.signals?.length) {
          setSignals(data.signals);
          setSignalsLive(true);
        } else {
          setSignals(SIGNALS_FALLBACK);
          setSignalsLive(false);
        }
      })
      .catch(() => {
        setSignals(SIGNALS_FALLBACK);
        setSignalsLive(false);
      })
      .finally(() => setSignalsLoading(false));
  }, [ticker]);

  useEffect(() => {
    setShownLines(0);
    const id = setInterval(() => {
      setShownLines(s => {
        if (s + 1 >= summaryLines.length) { clearInterval(id); return summaryLines.length; }
        return s + 1;
      });
    }, 600);
    return () => clearInterval(id);
  }, [prompt]);

  const faq = [
    { q: 'How fresh is the data?',         a: 'Yahoo Finance data is typically 15 minutes delayed for NSE. Indices update every ~1 minute via the Vite proxy in dev and Vercel serverless in production.' },
    { q: 'Is this financial advice?',       a: 'No. All content is for educational purposes only. Please consult a SEBI-registered adviser before investing.' },
    { q: 'Which exchanges are supported?',  a: 'NSE (National Stock Exchange) and BSE (Bombay Stock Exchange) via Yahoo Finance tickers (.NS suffix).' },
  ];

  return (
    <section className="pb-16 pt-8 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">

          {/* ── LEFT column ── */}
          <div className="space-y-5">

            {/* Summary card */}
            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-navy-500 uppercase tracking-widest">AI Analysis</span>
                  <span className="text-[10px] border border-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-mono">
                    {tickerLabel(ticker)}
                  </span>
                </div>
                <button
                  onClick={() => { setQuoteLoading(true); fetchStockQuote(ticker).then(q => { setQuote(q); setQuoteLoading(false); }); }}
                  className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-700 transition-colors"
                >
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              </div>

              {/* Streaming summary */}
              <div className="space-y-1 mb-5">
                {summaryLines.slice(0, shownLines + 1).map((line, i) => (
                  <p key={i} className="text-sm text-slate-600 leading-relaxed" style={{ opacity: i <= shownLines ? 1 : 0, transition: 'opacity 0.4s' }}>
                    {i === 0 ? <span className="text-slate-900 font-semibold">{line}</span> : line}
                  </p>
                ))}
              </div>

              {/* Snapshot metrics */}
              {quoteLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-xl animate-pulse bg-slate-100" />
                  ))}
                </div>
              ) : quote ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard label="Open"      value={formatPrice(quote.open)} />
                  <StatCard label="Day High"  value={formatPrice(quote.high)} />
                  <StatCard label="Day Low"   value={formatPrice(quote.low)}  />
                  <StatCard label="Prev Close" value={formatPrice(quote.prevClose)} />
                </div>
              ) : null}
            </Card>

            {/* Live chart */}
            <LiveChart ticker={ticker} quote={quote} />

            {/* Tabs: Technicals / News / FAQ */}
            <Card>
              {/* Tab bar */}
              <div className="flex border-b border-slate-100">
                {(['technicals','news','faq'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-3 text-xs font-semibold uppercase tracking-widest transition-colors ${
                      activeTab === tab
                        ? 'text-navy-500 border-b-2 border-navy-500 -mb-px'
                        : 'text-slate-400 hover:text-slate-700'
                    }`}
                  >
                    {tab === 'faq' ? 'FAQ' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div className="p-5">
                {activeTab === 'technicals' && (
                  <div>
                    {!signalsLive && !signalsLoading && (
                      <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-700">
                        ⚠ <strong>Illustrative layout only.</strong> Live signals are not available for this ticker (indices or data limit reached). Values below are examples only. Not investment advice.
                      </div>
                    )}
                    {signalsLive && (
                      <div className="mb-3 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-[11px] text-green-700">
                        ✓ Live data · TwelveData · NSE · 1-day timeframe · Educational only — not investment advice.
                      </div>
                    )}
                    {signalsLoading ? (
                      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="h-14 rounded-xl animate-pulse bg-slate-100" />
                        ))}
                      </div>
                    ) : (
                      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                        {signals.map(s => (
                          <div key={s.name} className="flex items-center justify-between rounded-xl p-3 border border-slate-100 bg-slate-50">
                            <div>
                              <div className="text-xs font-medium text-slate-800">{s.name}</div>
                              <div className="text-[11px] font-mono text-slate-500">{s.value}</div>
                            </div>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              s.state === 'Bullish' || s.state === 'Strong trend' ? 'bg-green-50 text-green-600' :
                              s.state === 'Bearish' || s.state === 'Overbought'  ? 'bg-red-50 text-red-600' :
                              s.state === 'Oversold'                              ? 'bg-green-50 text-green-600' :
                                                                                    'bg-slate-100 text-slate-500'
                            }`}>{s.state}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'news' && (
                  <div>
                  <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-700">
                    ⚠ <strong>Illustrative headlines only.</strong> These are sample market themes, not sourced from actual publications. Real news integration is coming soon. Not investment advice.
                  </div>
                  <div className="divide-y divide-slate-100">
                    {NEWS.map((n, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                        <div>
                          <div className="text-sm text-slate-800">{n.title}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{n.src} · {n.time} ago</div>
                        </div>
                        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          n.pos === true  ? 'bg-green-50 text-green-600' :
                          n.pos === false ? 'bg-red-50 text-red-600' :
                                            'bg-slate-100 text-slate-500'
                        }`}>
                          {n.pos === true ? 'Positive' : n.pos === false ? 'Negative' : 'Neutral'}
                        </span>
                      </div>
                    ))}
                  </div>
                  </div>
                )}

                {activeTab === 'faq' && (
                  <div className="space-y-2">
                    {faq.map((f, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                        <button
                          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-slate-800 text-left"
                          onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                        >
                          {f.q}
                          {faqOpen === i ? <ChevronUp className="h-4 w-4 text-navy-500 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                        </button>
                        {faqOpen === i && (
                          <div className="px-4 pb-3 text-sm text-slate-600 leading-relaxed bg-white border-t border-slate-100">{f.a}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <p className="text-[11px] text-slate-400 px-1">
              Data from Yahoo Finance · 15-min delayed · Not financial advice
            </p>
          </div>

          {/* ── RIGHT column ── */}
          <div className="space-y-4">

            {/* Quote summary */}
            {quoteLoading ? (
              <Card className="p-5 space-y-3">
                <div className="h-4 w-32 rounded animate-pulse bg-slate-100" />
                <div className="h-8 w-28 rounded animate-pulse bg-slate-100" />
                <div className="h-4 w-20 rounded animate-pulse bg-slate-100" />
              </Card>
            ) : quote ? (
              <Card className="p-5">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-1">{quote.shortName}</div>
                <div className="text-3xl font-mono font-bold text-slate-900 mb-1">{formatPrice(quote.price)}</div>
                <PriceBadge change={quote.change} changePct={quote.changePct} />
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  {[
                    { k: 'Volume',    v: quote.volume.toLocaleString('en-IN') },
                    { k: 'Mkt Cap',   v: formatIndianCurrency(quote.marketCap) },
                    { k: 'P/E',       v: quote.pe?.toFixed(1) ?? '—' },
                    { k: '52W High',  v: quote.week52High ? formatPrice(quote.week52High) : '—' },
                    { k: '52W Low',   v: quote.week52Low  ? formatPrice(quote.week52Low)  : '—' },
                  ].map(({ k, v }) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-mono text-slate-900 font-medium">{v}</span>
                    </div>
                  ))}
                </div>
                <button className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-navy-500 transition-colors">
                  <Bookmark className="h-3.5 w-3.5" /> Save to watchlist
                </button>
              </Card>
            ) : null}

            {/* Fundamentals */}
            {!ticker.startsWith('^') && (
              <FundamentalsPanel f={fundamentals} loading={fundLoading} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AiResponse;
